// From floor area to dwellings, parking and a compliance report.
import {
  ACCESSIBLE_SHARE,
  ACCESSIBLE_SOURCE,
  DEFAULT_PARKING_PER_DWELLING,
  EV_SHARE,
  EV_SOURCE,
  ESCAPE_SOURCE,
  MAX_DISTANCE_TO_STAIR,
  HIGH_BUILDING_HEIGHT,
  HIGH_BUILDING_SOURCE,
  LIFT_REQUIRED_ABOVE_STOREYS,
  LIFT_SOURCE,
  MIN_DWELLING_AREA,
  MIN_DWELLING_AREA_SOURCE,
  MIN_SETBACK_SOURCE,
  PARKING_SOURCE,
  SUNLIGHT_SOURCE,
  type Source,
} from "../rules/ro.ts";
import { area, distanceTo, type MultiPoly } from "./geom.ts";
import { overshadowing } from "./context.ts";
import { DEFAULT_LATITUDE } from "./sun.ts";
import { parkingPlan, rampZone, surfaceParking, type ParkingPlan } from "./parking.ts";
import { footpaths } from "./siteplan.ts";
import { union } from "./geom.ts";
import type { Envelope } from "./envelope.ts";
import { CUT_ABOVE_4_SOURCE, CUT_SOURCE, DEROGATION, EXISTING_SOURCE, HEIGHT_REGIME_SOURCE, POT_SOURCE } from "../rules/catuc.ts";
import { limits, type Indicators } from "./indicators.ts";
import type { Layout } from "./layout.ts";
import type { SitePlan } from "./siteplan.ts";
import type { Option } from "./massing.ts";
import type { Programme, Rules, Site, UnitType } from "./rules.ts";

export interface Dwellings {
  residentialGross: number;
  usable: number;
  /** `area` is the average usable area; min and belowMin come from a drawn layout. */
  byType: { type: UnitType; count: number; area: number; minUsable?: number; belowMin?: number }[];
  total: number;
  /** Whether the counts come from drawn floor plans or from an area estimate. */
  from: "layout" | "estimate";
}

/** Counts from the drawn floor plans: what the building actually holds. */
export function dwellingsFromLayout(o: Option, prog: Programme, l: Layout): Dwellings {
  const residentialGross = o.storeys
    .filter((s) => !(s.index === 0 && prog.groundUse === "commercial"))
    .reduce((t, s) => t + s.area, 0);
  return {
    residentialGross,
    usable: l.usable,
    byType: l.byType.map((b) => ({ type: b.type, count: b.count, area: b.avgUsable, minUsable: b.minUsable, belowMin: b.belowMin })),
    total: l.total,
    from: "layout",
  };
}

/**
 * Usable area through the efficiency ratio, then the mix. Counts are
 * floored per type, so the total never claims more area than there is.
 */
export function dwellings(o: Option, prog: Programme): Dwellings {
  const residentialGross = o.storeys
    .filter((s) => !(s.index === 0 && prog.groundUse === "commercial"))
    .reduce((t, s) => t + s.area, 0);
  const usable = residentialGross * prog.efficiency;
  const avg = prog.mix.reduce((t, m) => t + m.share * m.area, 0);
  const n = avg > 0 ? usable / avg : 0;
  const byType = prog.mix.map((m) => ({ type: m.type, count: Math.floor(n * m.share), area: m.area }));
  return { residentialGross, usable, byType, total: byType.reduce((t, b) => t + b.count, 0), from: "estimate" };
}

export interface Parking {
  required: number;
  perDwelling: number;
  fromRules: boolean;
  basementArea: number;
  perLevel: number;
  /** Basement levels. */
  levels: number;
  /** Spaces in all: surface + podium + basements. */
  capacity: number;
  surface: number;
  podium: number;
  underground: number;
  /** Required spaces that fit nowhere. */
  short: boolean;
  plan: ParkingPlan;
  surfaceStalls: MultiPoly[];
  surfaceAisles: MultiPoly;
  /** Of the spaces provided: for people with disabilities, and fitted for charging. */
  accessible: number;
  ev: number;
}

/**
 * Parking in the order it costs: at grade where the plot has room, then the
 * podium the programme asked for, then as many basements as the rest needs.
 * The requirement follows the norm by flat type when the PUZ gives one.
 */
export function parking(env: Envelope, rules: Rules, prog: Programme, d: Dwellings, site: Site, cores: MultiPoly, l: Layout, o: Option): Parking {
  const perDwelling = rules.parkingPerDwelling ?? DEFAULT_PARKING_PER_DWELLING;
  const byType = rules.parkingByType;
  const required = Math.ceil(byType
    ? d.byType.reduce((t, b) => t + b.count * (byType[b.type] ?? perDwelling), 0)
    : d.total * perDwelling);
  const mode = prog.parking ?? { surface: false, podiumLevels: 0, underground: true };

  const podium = l.floors.flatMap((f) => f.units).filter((u) => u.kind === "parking").reduce((t, u) => t + (u.stalls?.length ?? 0), 0);
  const surf = mode.surface
    ? surfaceParking(env, site, o.storeys[0].footprint, union(footpaths(env, site, l), rampZone(env, site)))
    : { stalls: [], aisles: [] };
  const remaining = Math.max(0, required - podium - surf.stalls.length);
  const plan = mode.underground ? parkingPlan(env, site, cores, remaining) : { perLevel: 0, levels: [], capacity: 0, short: remaining > 0 };
  const capacity = surf.stalls.length + podium + plan.capacity;
  return {
    required, perDwelling, fromRules: rules.parkingPerDwelling !== undefined || !!byType,
    basementArea: plan.levels[0] ? area(plan.levels[0].footprint) : 0,
    perLevel: plan.perLevel, levels: plan.levels.length, capacity,
    surface: surf.stalls.length, podium, underground: plan.capacity,
    short: capacity < required, plan, surfaceStalls: surf.stalls, surfaceAisles: surf.aisles,
    accessible: capacity ? Math.max(1, Math.ceil(capacity * ACCESSIBLE_SHARE)) : 0,
    ev: Math.ceil(capacity * EV_SHARE),
  };
}

export type Verdict = "pass" | "fail" | "info" | "not-checked";

export interface Check {
  rule: string;
  value: string;
  limit: string;
  verdict: Verdict;
  source: string;
  verified: boolean;
  note?: string;
}

const src = (s: Source) => [s.act, s.article].filter(Boolean).join(", ");
const fmt = (n: number, dp = 2) => n.toLocaleString("ro-RO", { maximumFractionDigits: dp });

export function compliance(env: Envelope, rules: Rules, o: Option, d: Dwellings, p: Parking, ind: Indicators, l: Layout, sp: SitePlan, site?: Site): Check[] {
  const local = (k: keyof NonNullable<Rules["sources"]>) => rules.sources?.[k] ?? "CU / PUZ";
  const regime = `P+${o.storeys.length - 1}E`;
  const lim = limits(rules, env.plotArea);
  const catuc = (rules.regime ?? "catuc") === "catuc";
  const der = DEROGATION[rules.derogation ?? "none"];
  const withDerogation = (base: string, v: number, dp: number, unit = "") =>
    lim.factor ? `≤ ${fmt(v, dp)}${unit} (${base}${unit} + ${Math.round(lim.factor * 100)}%)` : `≤ ${fmt(v, dp)}${unit}`;
  const checks: Check[] = [
    {
      rule: catuc ? "POT (proiecție, cu balcoane)" : "POT (amprentă la sol)",
      value: `${fmt(ind.pot, 1)}%`, limit: withDerogation(fmt(rules.pot, 1), lim.potLimit, 1, "%"),
      verdict: ind.pot <= lim.potLimit + 1e-6 ? "pass" : "fail",
      source: `${local("pot")}; ${src(catuc ? POT_SOURCE : { act: "Legea 350/2001 (abrogată)", article: "Anexa 2", verified: false })}`,
      verified: catuc ? POT_SOURCE.verified : false,
      note: catuc
        ? `${fmt(ind.balconies)} m² de balcoane în afara amprentei intră în proiecție. ${POT_SOURCE.note}`
        : "Plan aprobat înainte de 25.08.2026: se aplică definiția veche (amprenta la sol). Art. 582 păstrează procedurile începute sub legea veche.",
    },
    ...(l.balconiesRemoved ? [{
      rule: "Balcoane scoase pentru POT", value: `${l.balconiesRemoved} coloane`, limit: "POT cu balcoane ≤ limită", verdict: "info" as const, source: src(POT_SOURCE), verified: true,
      note: "Balcoanele desenate ar fi dus POT peste limită, așa că s-au scos coloane verticale întregi, începând cu cele mai mari.",
    }] : []),
    {
      rule: "CUT", value: fmt(ind.cut), limit: withDerogation(fmt(rules.cut), lim.cutLimit, 2),
      verdict: ind.cut <= lim.cutLimit + 1e-6 ? "pass" : "fail",
      source: `${local("cut")}; ${src(CUT_SOURCE)}`, verified: CUT_SOURCE.verified, note: CUT_SOURCE.note,
    },
    { rule: "Înălțime la cornișă", value: `${fmt(o.height, 1)} m`, limit: `≤ ${fmt(rules.maxHeight, 1)} m`, verdict: o.height <= rules.maxHeight + 1e-9 ? "pass" : "fail", source: local("maxHeight"), verified: true },
  ];
  if (rules.maxStoreys !== undefined) {
    checks.push({ rule: "Regim de înălțime", value: regime, limit: `≤ P+${rules.maxStoreys - 1}E`, verdict: o.storeys.length <= rules.maxStoreys ? "pass" : "fail", source: `${local("maxStoreys")}; ${src(HEIGHT_REGIME_SOURCE)}`, verified: true, note: HEIGHT_REGIME_SOURCE.note });
  }
  if (rules.cut > 4) {
    const ok = (rules.indicatorsFrom ?? "PUZ") === "PUG";
    checks.push({ rule: "CUT peste 4", value: fmt(rules.cut), limit: "numai prin PUG", verdict: ok ? "pass" : "fail", source: src(CUT_ABOVE_4_SOURCE), verified: true,
      note: ok ? undefined : "Un CUT peste 4 îl poate stabili doar PUG-ul cu regulamentul lui local, nu un PUZ sau un PUD." });
  }
  if (rules.existing && (rules.existing.footprint > 0 || rules.existing.gross > 0)) {
    checks.push({ rule: "Construcții existente păstrate", value: `${fmt(rules.existing.footprint)} m² Ac · ${fmt(rules.existing.gross)} m² Adc`, limit: "se adună la indicatori", verdict: "info", source: src(EXISTING_SOURCE), verified: true });
  }
  if (lim.factor) {
    checks.push({ rule: "Derogare simulată", value: der.label, limit: `o singură dată`, verdict: "info", source: src(der.source), verified: true,
      note: `Nu e un drept: cifrele de mai sus cer ${rules.derogation === "pud" ? "un PUD aprobat (prin dispoziția primarului)" : "un PUZ aprobat"}. ${der.condition}` });
  }
  // Setbacks hold by construction: every storey sits inside its own envelope.
  const sb = env.storeys[0].setbacks.map((s) => fmt(s, 1)).join(" / ");
  checks.push({ rule: "Retrageri (parter, pe fiecare latură)", value: `${sb} m`, limit: "din PUZ, minim Codul civil", verdict: "pass", source: `${local("setbacks")}; ${src(MIN_SETBACK_SOURCE)}`, verified: MIN_SETBACK_SOURCE.verified, note: "Respectat prin construcție: fiecare nivel stă în anvelopa de la înălțimea lui." });

  for (const b of d.byType) {
    if (b.count === 0) continue;
    const min = MIN_DWELLING_AREA[b.type];
    const smallest = b.minUsable ?? b.area;
    const value = b.belowMin ? `${b.belowMin} din ${b.count} sub minim (min ${fmt(smallest, 0)} m²)` : `min ${fmt(smallest, 0)} m²`;
    checks.push({ rule: `Suprafață utilă, ${b.type} cam.`, value, limit: `≥ ${min} m²`, verdict: smallest >= min ? "pass" : "fail", source: src(MIN_DWELLING_AREA_SOURCE), verified: MIN_DWELLING_AREA_SOURCE.verified, note: MIN_DWELLING_AREA_SOURCE.note });
  }

  const where = [p.surface && `${p.surface} la sol`, p.podium && `${p.podium} pe podium`, p.underground && `${p.underground} în ${p.levels} ${p.levels === 1 ? "subsol" : "subsoluri"}`].filter(Boolean).join(" + ") || "niciunul";
  if (p.podium) checks.push({ rule: "Parcare pe podium în CUT", value: `${o.storeys.filter((s) => l.floors[s.index]?.units.some((u) => u.kind === "parking")).length} niveluri`, limit: "se socotește în CUT și în regimul de înălțime", verdict: "info", source: src(CUT_SOURCE), verified: true, note: "Legea 169/2026 scade din CUT doar parcarea din subsol și demisol: un podium consumă suprafață pe care ar fi putut-o avea apartamentele." });
  checks.push({ rule: "Locuri pentru persoane cu dizabilități", value: `${p.accessible}`, limit: `≥ ${Math.round(ACCESSIBLE_SHARE * 100)}%, minim 1`, verdict: "info", source: src(ACCESSIBLE_SOURCE), verified: false, note: ACCESSIBLE_SOURCE.note });
  checks.push({ rule: "Locuri echipate pentru încărcare electrică", value: `${p.ev}`, limit: `${Math.round(EV_SHARE * 100)}% (de verificat)`, verdict: "info", source: src(EV_SOURCE), verified: false, note: EV_SOURCE.note });
  checks.push({ rule: "Locuri de parcare", value: `${p.capacity}: ${where}${p.short ? " (nu încap)" : ""}`, limit: `≥ ${p.required} (${fmt(p.perDwelling)}/locuință)`, verdict: p.capacity >= p.required ? "pass" : "fail", source: p.fromRules ? local("parking") : src(PARKING_SOURCE), verified: p.fromRules || PARKING_SOURCE.verified, note: p.fromRules ? undefined : PARKING_SOURCE.note });

  checks.push({ rule: "Clădire înaltă", value: `${fmt(o.height, 1)} m`, limit: `> ${HIGH_BUILDING_HEIGHT} m înseamnă clădire înaltă`, verdict: "info", source: src(HIGH_BUILDING_SOURCE), verified: HIGH_BUILDING_SOURCE.verified, note: o.height > HIGH_BUILDING_HEIGHT ? "Clădire înaltă: cerințele P118 pentru scări, desfumare și accesul pompierilor." : undefined });
  checks.push({ rule: "Ascensor", value: regime, limit: `obligatoriu peste P+${LIFT_REQUIRED_ABOVE_STOREYS - 1}E`, verdict: "info", source: src(LIFT_SOURCE), verified: LIFT_SOURCE.verified, note: o.storeys.length > LIFT_REQUIRED_ABOVE_STOREYS ? "Lift obligatoriu." : undefined });
  const greenPct = (sp.balance.green / sp.balance.plot) * 100;
  const naturalPct = (sp.balance.greenNatural / sp.balance.plot) * 100;
  checks.push({
    rule: "Spații verzi", value: `${fmt(greenPct, 1)}% (${fmt(naturalPct, 1)}% pe teren natural)`,
    limit: rules.minGreen !== undefined ? `≥ ${fmt(rules.minGreen, 0)}%` : "nespecificat în CU / PUZ",
    verdict: rules.minGreen === undefined ? "info" : greenPct >= rules.minGreen ? "pass" : "fail",
    source: local("green"), verified: rules.minGreen !== undefined,
    note: "Spațiul verde e ce rămâne după clădire, rampa deschisă și alei. Cea mai mare parte stă peste subsol; verifică dacă regula locală cere o parte pe teren natural.",
  });
  checks.push({
    rule: "Acces auto", value: sp.vehicleAccess ? "din stradă, prin rampa subsolului" : "lipsă",
    limit: "acces carosabil din drum public", verdict: sp.vehicleAccess || p.required === 0 ? "info" : "fail", source: "CU / PUZ", verified: true,
    note: "O rampă cu dublu sens la capătul frontului. Accesul pompierilor și lățimea lui nu sunt încă verificate.",
  });
  // escape: the farthest front door from any stair, straight line, worst floor
  let farthest = 0;
  for (const f of l.floors) {
    const stairs = f.units.flatMap((u) => u.parts ?? []).filter((p) => p.kind === "stair").map((p) => p.polygon);
    if (!stairs.length) continue;
    for (const u of f.units) if (u.kind === "apartment" && u.door) farthest = Math.max(farthest, Math.min(...stairs.map((st) => distanceTo(u.door!, st))));
  }
  checks.push({
    rule: "Distanța ușă–scară", value: `max ${fmt(farthest, 1)} m`, limit: `≤ ${MAX_DISTANCE_TO_STAIR} m`,
    verdict: farthest <= MAX_DISTANCE_TO_STAIR ? "pass" : "fail", source: src(ESCAPE_SOURCE), verified: ESCAPE_SOURCE.verified, note: ESCAPE_SOURCE.note,
  });
  // facing windows: the closest pair of windows that look straight at each other across open air
  let facing = Infinity;
  for (const f of l.floors) {
    const wins = f.units.filter((u) => u.kind === "apartment").flatMap((u) => (u.windows ?? []).map((w) => ({ w, u })));
    for (let i = 0; i < wins.length; i++) for (let j = i + 1; j < wins.length; j++) {
      const A = wins[i], B = wins[j];
      if (A.u === B.u) continue;
      const dot = A.w.normal[0] * B.w.normal[0] + A.w.normal[1] * B.w.normal[1];
      if (dot > -0.9) continue;
      const dx = B.w.at[0] - A.w.at[0], dy = B.w.at[1] - A.w.at[1];
      const along = dx * A.w.normal[0] + dy * A.w.normal[1]; // B in front of A
      const side = Math.abs(dx * A.w.normal[1] - dy * A.w.normal[0]);
      if (along > 0.5 && side < 6) facing = Math.min(facing, along);
    }
  }
  if (Number.isFinite(facing)) {
    const need = Math.max(6, o.height / 2);
    checks.push({ rule: "Distanța între fațade care se privesc", value: `${fmt(facing, 1)} m`, limit: `≥ ${fmt(need, 1)} m (H/2, min. 6 m)`, verdict: facing >= need ? "pass" : "fail",
      source: "RLU / PUZ (regula uzuală, de verificat)", verified: false, note: "Între ferestrele aceleiași clădiri care se privesc, de ex. peste o curte. Multe regulamente locale cer jumătate din înălțimea fațadei mai înalte." });
  }
  // our shadow on the neighbours, when they are known (OMS 119/2014 protects their sun too)
  if (site?.context?.length) {
    const o3 = overshadowing(site.context.map((m) => ({ ...m, heightFrom: m.heightFrom ?? "assumed" })), o.storeys.map((s) => ({ footprint: s.footprint, base: s.base, top: s.cornice })), env.plot, site.latitude ?? DEFAULT_LATITUDE);
    checks.push({
      rule: "Umbrirea clădirilor vecine", value: o3.lost ? `${o3.lost} din ${o3.points} puncte de fațadă sub 1½ h` : `niciun punct din ${o3.points} sub 1½ h din cauza noastră`,
      limit: "vecinii păstrează 1½ h la solstițiul de iarnă", verdict: o3.lost ? "fail" : "pass", source: src(SUNLIGHT_SOURCE), verified: SUNLIGHT_SOURCE.verified,
      note: `Puncte la fiecare 4 m pe fațadele vecine orientate spre teren, la 1,5 m, cu și fără clădirea noastră.${o3.lostOnAssumed ? ` ${o3.lostOnAssumed} dintre punctele pierdute sunt pe clădiri a căror înălțime OpenStreetMap nu o dă (presupusă 10 m).` : ""} Vecinii vin din OpenStreetMap și pot fi incompleți.`,
    });
  }
  // street noise, as a proxy: bedrooms whose window faces the street within 25 m of it
  if (site) {
    const ring = env.plot[0][0];
    const street = ring.map((a, i) => [a, ring[(i + 1) % ring.length], site.edges[i]] as const).filter(([, , k]) => k === "aliniament");
    let beds = 0, loud = 0;
    for (const f of l.floors) for (const u of f.units) if (u.kind === "apartment") for (const r of u.rooms ?? []) {
      if (r.kind !== "bedroom") continue;
      beds++;
      const win = (u.openings ?? []).find((op) => op.kind === "window" && distanceTo([(op.a[0] + op.b[0]) / 2, (op.a[1] + op.b[1]) / 2], r.polygon) < 0.1);
      if (!win) continue;
      const m: [number, number] = [(win.a[0] + win.b[0]) / 2, (win.a[1] + win.b[1]) / 2];
      if (street.some(([a, b]) => {
        const len = Math.hypot(b[0] - a[0], b[1] - a[1]), nx = (b[1] - a[1]) / len, ny = -(b[0] - a[0]) / len; // outward
        const dist = Math.abs((m[0] - a[0]) * nx + (m[1] - a[1]) * ny);
        const wx = win.b[0] - win.a[0], wy = win.b[1] - win.a[1];
        return dist < 25 && Math.abs(wx * nx + wy * ny) / Math.hypot(wx, wy) < 0.3; // parallel to the street
      })) loud++;
    }
    checks.push({ rule: "Dormitoare cu fereastra spre stradă", value: `${loud} din ${beds}`, limit: "cât mai puține", verdict: "info", source: "indicator de zgomot, nu normă", verified: true,
      note: "Dormitoare cu fereastra paralelă cu strada și la cel mult 25 m de ea. Un indiciu pentru zgomotul din trafic până se încarcă o hartă de zgomot; unde e mare, cere vitraj acustic." });
  }
  checks.push({
    rule: "Însorire camera de zi", value: l.sunless ? `${l.sunless} din ${l.total} sub 1½ h` : `toate ${l.total} ≥ 1½ h`,
    limit: "≥ 1½ h la solstițiul de iarnă", verdict: l.sunless ? "fail" : "pass", source: src(SUNLIGHT_SOURCE), verified: SUNLIGHT_SOURCE.verified,
    note: site?.context?.length
      ? `Calculat pe 21 decembrie la latitudinea terenului, cu umbra clădirii noastre și ${site.context.length} clădiri vecine din OpenStreetMap (înălțimile lor parțial presupuse).`
      : "Calculat cu umbra clădirii noastre, pe 21 decembrie, la latitudinea terenului. Vecinii nu sunt încărcați, deci e o condiție necesară, nu suficientă: încarcă-i din OpenStreetMap, sub teren.",
  });
  return checks;
}
